<script setup lang="ts">
import { ref, watch, onBeforeUnmount, type PropType } from 'vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import type { MobileAppModel } from '@/utils';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  app: {
    type: Object as PropType<MobileAppModel | null>,
    default: null
  },
  /** Product the app belongs to, e.g. "SP Data" or "PUMP". */
  productName: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['update:visible']);

const qrDataUrl = ref('');
const generating = ref(false);
const failed = ref(false);
const copied = ref(false);
const copyFailed = ref(false);

let copiedTimer: ReturnType<typeof setTimeout> | undefined;

// Rendered at 2x the display size so the code stays sharp, and scannable, on retina screens.
const QR_PIXELS = 440;

const generate = async (app: MobileAppModel) => {
  generating.value = true;
  failed.value = false;

  try {
    // Loaded on demand — the encoder is only needed once someone opens this dialog.
    const { toDataURL } = await import('qrcode');
    qrDataUrl.value = await toDataURL(app.url, {
      width: QR_PIXELS,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#1f2937', light: '#ffffff' }
    });
  } catch {
    // The link and its copy button below are still usable, so this degrades rather than blocks.
    failed.value = true;
    qrDataUrl.value = '';
  } finally {
    generating.value = false;
  }
};

watch(
  () => [props.visible, props.app?.id] as const,
  () => {
    copied.value = false;
    if (props.visible && props.app) {
      void generate(props.app);
    }
  },
  { immediate: true }
);

const copyLink = async () => {
  if (!props.app) {
    return;
  }

  clearTimeout(copiedTimer);
  copied.value = false;
  copyFailed.value = false;

  try {
    await navigator.clipboard.writeText(props.app.url);
    copied.value = true;
  } catch {
    // Clipboard access can be blocked by permissions or a non-secure origin. Say so rather than
    // letting the click look like it did nothing — the URL above is still there to select.
    copyFailed.value = true;
  }

  copiedTimer = setTimeout(() => {
    copied.value = false;
    copyFailed.value = false;
  }, 3000);
};

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <Dialog :visible="visible"
    modal
    dismissable-mask
    :draggable="false"
    :style="{ width: '360px' }"
    :breakpoints="{ '480px': '92vw' }"
    @update:visible="emit('update:visible', $event)">
    <template #header>
      <div class="qr-dialog__header">
        <FontAwesomeIcon v-if="app" :icon="['fab', app.icon]" class="qr-dialog__header-icon" />
        <span class="qr-dialog__title">Get {{ productName }} for {{ app?.name }}</span>
      </div>
    </template>

    <div v-if="app" class="qr-dialog">
      <p class="qr-dialog__hint">
        Scan this code with your phone's camera to open the listing on {{ app.store }}.
      </p>

      <div class="qr-dialog__code">
        <ProgressSpinner v-if="generating" stroke-width="4" style="width: 48px; height: 48px" />
        <img v-else-if="qrDataUrl"
          :src="qrDataUrl"
          :alt="`QR code linking to the ${productName} ${app.name} app on ${app.store}`"
          class="qr-dialog__image" />
        <p v-else-if="failed" class="qr-dialog__error">
          Couldn't render the code — use the link below instead.
        </p>
      </div>

      <p class="qr-dialog__url">{{ app.url }}</p>

      <div class="qr-dialog__actions">
        <button type="button" class="qr-dialog__button" @click="copyLink">
          {{ copied ? 'Link copied' : 'Copy link' }}
        </button>
        <a class="qr-dialog__button qr-dialog__button--primary"
          :href="app.url"
          target="_blank"
          rel="noopener noreferrer">
          Open {{ app.store }}
        </a>
      </div>

      <p v-if="copyFailed" class="qr-dialog__copy-failed">
        Couldn't copy automatically — select the link above instead.
      </p>

      <span class="qr-dialog__status" role="status" aria-live="polite">
        {{ copied ? 'Link copied to clipboard' : '' }}
      </span>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@use '../../assets/variables.module' as *;
.qr-dialog__header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.qr-dialog__header-icon {
  font-size: 20px;
  color: hsl(210, 11%, 25%);
}
.qr-dialog__title {
  font-size: 16px;
  font-weight: 600;
}
.qr-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.qr-dialog__hint {
  margin: 0;
  color: hsl(210, 9%, 40%);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}
.qr-dialog__code {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 220px;
  height: 220px;
  padding: 10px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 12px;
  background-color: white;
}
.qr-dialog__image {
  width: 100%;
  height: 100%;
  // Keeps the modules crisp instead of blurring them, which hurts scanning.
  image-rendering: pixelated;
}
.qr-dialog__error {
  margin: 0;
  padding: 0 12px;
  color: hsl(210, 9%, 40%);
  font-size: 13px;
  text-align: center;
}
.qr-dialog__url {
  margin: 0;
  color: hsl(210, 9%, 50%);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  word-break: break-all;
}
.qr-dialog__actions {
  display: flex;
  gap: 8px;
  width: 100%;
}
.qr-dialog__button {
  flex: 1;
  padding: 9px 12px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 8px;
  background-color: white;
  color: hsl(210, 11%, 15%);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background-color: hsl(210, 20%, 96%);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 1px;
  }
}
.qr-dialog__button--primary {
  border-color: transparent;
  background-color: $primary-color;
  color: white;

  &:hover {
    background-color: hsl(248.5, 100%, 46%);
  }
}
.qr-dialog__copy-failed {
  margin: -8px 0 0;
  color: hsl(0, 65%, 45%);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
}
// Announced to screen readers without taking up layout space.
.qr-dialog__status {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
